/**
 * Application Insights telemetry backend
 *
 * Wraps the App Insights singleton from appInsights.ts into the
 * TelemetryBackend interface so the logger can delegate to it.
 */

import type { TelemetryBackend } from "./types";
import type { TelemetryUser, DependencyData } from "../types";

import { SeverityLevel } from "@microsoft/applicationinsights-web";

import { LogLevel } from "../types";
import { getAppInsights } from "../appInsights";

const SEVERITY_MAP: Record<LogLevel, SeverityLevel> = {
  [LogLevel.DEBUG]: SeverityLevel.Verbose,
  [LogLevel.INFO]: SeverityLevel.Information,
  [LogLevel.WARN]: SeverityLevel.Warning,
  [LogLevel.ERROR]: SeverityLevel.Error,
  [LogLevel.CRITICAL]: SeverityLevel.Critical,
};

let currentUser: TelemetryUser | null = null;
let telemetryInitializerRegistered = false;

export function createAppInsightsBackend(): TelemetryBackend {
  return {
    name: "AppInsights",

    trace(level, message, properties) {
      const ai = getAppInsights();

      if (!ai) return;

      ai.trackTrace({
        message,
        severityLevel: SEVERITY_MAP[level],
        properties: properties as Record<string, string>,
      });
    },

    trackException(error, properties, componentStack) {
      const ai = getAppInsights();

      if (!ai) return;

      ai.trackException({
        exception: error,
        properties: {
          ...properties,
          ...(componentStack ? { componentStack } : {}),
        } as Record<string, string>,
      });
    },

    trackEvent(name, properties) {
      const ai = getAppInsights();

      if (!ai) return;

      ai.trackEvent({ name, properties: properties as Record<string, string> });
    },

    trackDependency(data: DependencyData) {
      const ai = getAppInsights();

      if (!ai) return;

      let target: string;

      try {
        target = new URL(data.url).host;
      } catch {
        target = data.url;
      }

      ai.trackDependencyData({
        id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
        name: data.name,
        target,
        data: data.url,
        duration: data.duration,
        responseCode: data.statusCode,
        success: data.success,
        type: "HTTP",
        properties: data.properties as Record<string, string>,
      });
    },

    trackPageView(name, uri) {
      const ai = getAppInsights();

      if (!ai) return;

      ai.trackPageView({ name, uri });
    },

    setUser(user) {
      currentUser = user;

      const ai = getAppInsights();

      if (ai) {
        ai.setAuthenticatedUserContext(user.id, user.tenant, true);

        if (!telemetryInitializerRegistered) {
          telemetryInitializerRegistered = true;
          ai.addTelemetryInitializer((item) => {
            if (item.baseData?.properties && currentUser) {
              item.baseData.properties.isEmbedded = String(currentUser.isEmbedded);
            }
          });
        }
      }
    },

    clearUser() {
      currentUser = null;

      const ai = getAppInsights();

      if (!ai) return;

      ai.clearAuthenticatedUserContext();
    },

    flush() {
      const ai = getAppInsights();

      if (!ai) return;

      ai.flush();
    },
  };
}
